import React from "react";
import * as Icons from "lucide-react";
import { ChevronRight, TrendingUp, TrendingDown, Minus, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";

const statusColor = {
  operational: "bg-emerald-500",
  degraded: "bg-amber-500",
  down: "bg-rose-500",
};

const badgeStyle = (b) => {
  if (!b) return null;
  const bu = String(b).toUpperCase();
  if (bu === "NEW") return "bg-emerald-500 text-white border-emerald-500";
  if (bu === "BETA") return "bg-primary text-primary-foreground border-primary";
  if (bu === "ON DECK") return "bg-amber-500 text-white border-amber-500";
  if (bu === "ROADMAP") return "bg-sky-500 text-white border-sky-500";
  if (bu === "FUTURE") return "bg-slate-500 text-white border-slate-500";
  // Counts like 99+
  return "bg-rose-500 text-white border-rose-500";
};

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
  if (trend === "down") return <TrendingDown className="w-3.5 h-3.5 text-rose-500" />;
  return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
}

/**
 * AppTile
 * @param {"list"|"compact"} variant  - visual density
 */
export default function AppTile({ app, variant = "list" }) {
  const Icon = Icons[app.icon] || Icons.LayoutGrid;
  const badgeLabel = app.status_badge || app.badge;
  const url = app.url || "#";
  const isInternal = typeof url === "string" && url.startsWith("/");
  const commonAnchorProps = isInternal
    ? { as: Link, to: url }
    : { as: "a", href: url, target: "_blank", rel: "noopener noreferrer" };

  const Wrapper = isInternal ? Link : "a";
  const wrapperProps = isInternal
    ? { to: url, title: app.name }
    : { href: url, title: app.name, target: "_blank", rel: "noopener noreferrer" };

  if (variant === "compact") {
    return (
      <Wrapper
        {...wrapperProps}
        className={cn(
          "card-lift group relative flex flex-col items-center justify-center gap-1",
          "rounded-lg border border-border bg-card p-2 pt-2.5 min-h-[92px]",
          "hover:border-primary/50"
        )}
      >
        {badgeLabel && (
          <span
            className={cn(
              "absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1 py-0.5 rounded border shadow-sm uppercase tracking-wide leading-none",
              badgeStyle(badgeLabel)
            )}
          >
            {badgeLabel}
          </span>
        )}
        {app.internal_only && (
          <span className="absolute top-1 left-1 text-muted-foreground" title="Internal only (IP-locked)">
            <Lock className="w-2.5 h-2.5" />
          </span>
        )}
        <div className="relative">
          {app.icon_image ? (
            <img
              src={app.icon_image}
              alt=""
              onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex"; }}
              className="w-11 h-11 rounded-lg object-cover"
            />
          ) : null}
          <div
            className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"
            style={{ display: app.icon_image ? "none" : "flex" }}
          >
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <span
            className={cn(
              "absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-card",
              statusColor[app.status] || "bg-muted-foreground"
            )}
          />
        </div>
        <div className="text-[10px] font-semibold text-foreground text-center leading-tight line-clamp-2 px-0.5">
          {app.name}
        </div>
      </Wrapper>
    );
  }

  // list variant (existing)
  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        "card-lift group relative flex items-start gap-3 rounded-xl border border-border bg-card p-4 pr-10",
        "hover:border-primary/50"
      )}
    >
      <div className="relative shrink-0 w-11 h-11 rounded-lg bg-primary/10 dark:bg-primary/15 border border-primary/20 flex items-center justify-center">
        {app.icon_image ? (
          <img src={app.icon_image} alt="" className="w-9 h-9 rounded-md object-cover" />
        ) : (
          <Icon className="w-5 h-5 text-primary" />
        )}
        <span
          className={cn(
            "absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-card",
            statusColor[app.status] || "bg-muted-foreground"
          )}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-[14px] text-foreground truncate">{app.name}</div>
          {badgeLabel && (
            <span
              className={cn(
                "text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wide",
                badgeStyle(badgeLabel)
              )}
            >
              {badgeLabel}
            </span>
          )}
          {app.internal_only && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
              <Lock className="w-3 h-3" /> IP
            </span>
          )}
        </div>
        <div className="text-[12.5px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
          {app.description}
        </div>
        {app.metric_value && (
          <div className="mt-2 flex items-center gap-1.5 text-[11.5px]">
            <TrendIcon trend={app.metric_trend} />
            <span className="text-muted-foreground">{app.metric_label}:</span>
            <span className="font-semibold text-foreground">{app.metric_value}</span>
          </div>
        )}
      </div>
      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </Wrapper>
  );
}
